import React, { useEffect, useCallback, useRef } from 'react';
import {
  useLocation, useNavigate, useParams, useSearchParams,
} from 'react-router';
import { LearningModuleModal } from 'src/components/learning-module';
import { EntityType } from 'src/constants/entity';
import LearningPathway from 'src/components/learning-pathway';
import { ROUTE_SEARCH_PARAM } from 'src/constants/route';
import { ClientLog } from 'src/constants/clientLog';
import { useAppSelector } from 'src/redux/hooks';
import { useGetLearningPathwayQuery, useGetLearningPathwayStaticQuery } from 'src/redux/slices/api/su';
import { selectLearningPathwayById } from 'src/redux/slices/learningPathways';
import { useClientId, useSelLocale, UNGATED_CLIENT_ID } from 'src/contexts/ClientContext';
import { selectIsWeblabInT1 } from 'src/redux/slices/weblabs';
import { WeblabId } from 'src/constants/weblab';
import { AppState } from 'src/redux/store';
import ClientLogger from 'src/utils/logger';
import useMetrics from 'src/metrics/useUserMetrics';
import usePagePerformance from 'src/metrics/usePagePerformance';
import { FeatureName } from 'src/metrics/metricsFields';
import LoadingView from 'src/components/LoadingView';
import ErrorView from 'src/components/ErrorView';
import { removeSearchParam } from 'src/utils/searchParams';
import { getStatusCodeFromError } from 'src/utils/error';

const LearningPathwayPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { learningPathwayId } = useParams();
  const learningModuleId = searchParams.get(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID);
  const { viewMetric } = useMetrics();
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const isPolyMigrationT1 = useAppSelector(
    (state: AppState) => selectIsWeblabInT1(state, WeblabId.NalandaPolyMigration),
  );
  // Under T1, ungated users also call the PolyD learning-pathway API
  // instead of the static endpoint, for unified content delivery.
  const usePolyD = clientId !== UNGATED_CLIENT_ID || isPolyMigrationT1;

  if (!learningPathwayId) {
    const errorMessage = `Expected learningPathwayId to be present in browser URL path: ${location.pathname}`;
    const error = new Error(errorMessage);
    ClientLogger.error(ClientLog.IllegalInternalDataState, error, {
      path: location.pathname,
      url: window.location.href,
    });
    throw error;
  }

  // Call appropriate API based on client type (skip the other)
  const polyDResult = useGetLearningPathwayQuery(
    { learningPathwayId, clientId, selLocale },
    { skip: !usePolyD },
  );
  const staticResult = useGetLearningPathwayStaticQuery(
    { learningPathwayId, clientId, selLocale },
    { skip: usePolyD },
  );
  const {
    isLoading, isError, error, data, isUninitialized,
  } = usePolyD ? polyDResult : staticResult;

  // Read the cached pathway from the slice to detect an inconsistent state where the stats
  // advertise content (sections/modules) but no sections are actually available to render.
  // The body is rendered from this same slice entry (see src/components/learning-pathway/index.tsx),
  // so an empty `sections` alongside non-zero stats yields a hero touting counts over an empty
  // accordion, with no error surfaced to the user.
  // Only evaluate once this page's own query has settled successfully: while the full fetch is
  // in flight the slice may legitimately hold a preview entry (from the homepage carousel or a
  // topic banner) that has stats but no sections yet.
  const pathway = useAppSelector((state) => selectLearningPathwayById(state, learningPathwayId));
  const stats = pathway?.data?.learningPathwayStats;
  const renderedSections = pathway?.data?.sections?.length ?? 0;
  const expectsContent = (stats?.totalSections ?? 0) > 0 || (stats?.totalModules ?? 0) > 0;
  const querySettled = !isLoading && !isError && !isUninitialized;
  const isInconsistentState = querySettled && pathway !== undefined && expectsContent && renderedSections === 0;

  // The inconsistent-state guard renders an error page, so it must count as a failed load in
  // availability and must not emit a successful page view.
  const isPageFailure = isError || isInconsistentState;

  // Track per-page performance (latency + availability) once the pathway finishes loading.
  // learningPathwayId is the resource key so client-side navigation between pathways (which reuses
  // this component instance) resets the measurement and re-emits metrics for each pathway.
  usePagePerformance(FeatureName.LearningPathwayPage, isLoading, isPageFailure, learningPathwayId);

  // Log the inconsistent state exactly once per pathway id. This component instance is reused
  // across client-side navigation between pathways, so track every id already logged (not just
  // the most recent) to avoid re-emitting for A -> B -> A, and to avoid re-emitting on plain
  // re-renders (e.g. search-param changes) for the same pathway.
  const loggedInconsistentPathwayIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (isInconsistentState && !loggedInconsistentPathwayIds.current.has(learningPathwayId)) {
      loggedInconsistentPathwayIds.current.add(learningPathwayId);
      ClientLogger.error(ClientLog.IllegalInternalDataState, {
        learningPathwayId,
        clientId,
        path: location.pathname,
        url: window.location.href,
        totalSections: stats?.totalSections,
        totalModules: stats?.totalModules,
        renderedSections,
        reason: 'stats report content but no sections are available to render',
      });
    }
  }, [isInconsistentState, learningPathwayId, clientId, location.pathname, stats, renderedSections]);

  const learningModuleInPathway = data?.learningPathway?.sections?.some(
    (section) => section.moduleList?.some((module) => module.moduleId === learningModuleId),
  );

  // Use useEffect to track page view after component mounts
  useEffect(() => {
    if (!isLoading && !isPageFailure) {
      viewMetric({
        feature: FeatureName.LearningPathwayPage,
        metadata: {
          pathwayId: learningPathwayId,
          clientId,
          path: location.pathname,
        },
      });
    }
  }, [isLoading, isPageFailure, viewMetric, learningPathwayId, clientId, location.pathname]);

  useEffect(() => {
    if (!isLoading && !isError && !isUninitialized && !data?.learningPathway) {
      ClientLogger.error(ClientLog.PayloadProcessingError, {
        message: 'Learning pathway data unavailable after successful fetch',
        learningPathwayId,
        clientId,
        url: window.location.href,
      });
    }
  }, [isLoading, isError, isUninitialized, data, learningPathwayId, clientId]);

  const appendModuleSearchParam = (moduleId: string) => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID, moduleId);
    navigate({ search: updatedSearchParams.toString() }, { replace: true });
  };

  const removeModuleSearchParam = useCallback(() => {
    navigate({ search: removeSearchParam(searchParams, ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID) }, { replace: true });
  }, [navigate, searchParams]);

  if (isLoading) {
    return <LoadingView />;
  }

  if (isError) {
    const statusCode = getStatusCodeFromError(error);
    const logContext = {
      learningPathwayId,
      clientId,
      path: location.pathname,
      url: window.location.href,
    };
    if (statusCode !== undefined && statusCode >= 400 && statusCode < 500) {
      ClientLogger.warn(ClientLog.IllegalInternalDataState, logContext);
    } else {
      ClientLogger.error(ClientLog.IllegalInternalDataState, logContext);
    }
    return <ErrorView statusCode={statusCode} />;
  }

  // Inconsistent-state guard: the pathway exists but its stats claim content while no sections
  // are available to render. Render the generic (non-redirecting) ErrorView deliberately — passing
  // no statusCode avoids the 4xx countdown-redirect to /learn, which would silently bounce the user
  // away and hide this internal data failure instead of surfacing it. (Logged once via the effect above.)
  if (isInconsistentState) {
    return <ErrorView />;
  }

  return (
    <div>
      <LearningPathway learningPathwayId={learningPathwayId} onModuleSelect={appendModuleSearchParam} />
      {learningModuleId && learningModuleInPathway && (
      <LearningModuleModal
        learningModuleId={learningModuleId}
        onModalClose={removeModuleSearchParam}
        parentId={learningPathwayId}
        parentType={EntityType.LearningPathway}
      />
      )}
    </div>
  );
};

export default LearningPathwayPage;
